import _ from "lodash"
import React, {useState, useEffect, useRef} from 'react';
import {createClient} from 'contentful';

import { makeStyles } from '@material-ui/core/styles';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import Hidden from '@material-ui/core/Hidden';
import AppBar from '@material-ui/core/AppBar';

import { DoctorDetail } from "./DoctorDetail.js"
import { Landing } from "./Landing.js"
import { About } from "./About.js"
import { DesktopMapView } from "./DesktopMapView.js"
import { MobileWebMapView } from "./MobileWebMapView.js"
import { Footer } from "./Footer.js"
import header from './headerLogo.png';
import colors from './colors.js';

import './App.css';

export const mixpanel = window.mixpanel;

const client = createClient({
  // This is the space ID. A space is like a project folder in Contentful terms
  space: '5faj6a32yjuj',
  // This is the access token for this space. Normally you get both ID and the token in the Contentful web app
  accessToken: 'po4EEv7AxNJrSonphiQca27kxebutJsfrR6hOEQBYS4',
});

const useStyles = makeStyles(theme => ({
  headerImgButton: {
    marginRight: theme.spacing(3),
  },
  headerAboutButton: {
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(1.7),
    fontSize: "18px",
    margin: 0,
    color: colors.pink,
    textDecoration: "none",
  },
  root: {
    height: '100%',
    backgroundColor: colors.blue
  },
  header: {
    height: theme.spacing(8),
    backgroundColor: colors.blue,
    paddingLeft: theme.spacing(3),
    paddingTop: theme.spacing(1.5),
    boxShadow: `0 0 0px`,
    flexGrow: 1,
    display: "flex",
  },
  headerImg: {
    width: '130px',
  },
}));

const DEFAULT_CENTER = {lat: 39.8283, lng: -98.5795}; // geographic center of US


export const App = ()  => {
  const scrollToRef = (ref) => window.scrollTo({
    left: 0,
    top: ref.current.offsetTop - 64,
    behavior: "smooth",
  });
  const [selectedDoctorId, setSelectedDoctorId] = React.useState(
    window.location.pathname.slice(1),
  );

  React.useEffect(() => {
    const handlePopState = () => {
      setSelectedDoctorId(
        window.location.pathname.slice(1),
      );
    };
    mixpanel.track("page_load");

    let vh = window.innerHeight * 0.01;
    // Then we set the value in the --vh custom property to the root of the document
    document.documentElement.style.setProperty('--vh', `${vh}px`);

    // We listen to the resize event
    window.addEventListener('resize', () => {
      // We execute the same script as before
      let vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    });
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const mapContainerRef = useRef(null)
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const [hoverDoctor, setHoverDoctor] = useState(null);
  const [currentCenter, setCurrentCenter] = useState(DEFAULT_CENTER);
  const [currentZoom, setZoom] = useState(4);
  const [lastZoom, setLastZoom] = useState(null);
  const [landingCopy, setLandingCopy] = useState({});
  const [aboutCopy, setAboutCopy] = useState({});
  const [mapCopy, setMapCopy] = useState({});
  const [doctors, setDoctors] = useState(new Map());
  const doctorsList = Array.from(doctors.values());
  // Hack to override the zoom on mobile
  // Somehow it doesn't reset the map when you manually zoom to 4 on mobile
  if (currentZoom === 4 && matches) {
    setZoom(3);
  }

  useEffect(() => {
    client.getEntries().then(entries => {
      const doctorEntries = _.filter(entries.items, e => e.sys.contentType.sys.id === "doctor");
      const landingCopyEntry = _.first(_.filter(entries.items, e => e.sys.contentType.sys.id === "landingContent"));
      setLandingCopy(landingCopyEntry.fields);
      const aboutCopyEntry = _.first(_.filter(entries.items, e => e.sys.contentType.sys.id === "aboutContent"));
      setAboutCopy(aboutCopyEntry.fields);
      const mapCopyEntry = _.first(_.filter(entries.items, e => e.sys.contentType.sys.id === "mapCopy"));
      setMapCopy(mapCopyEntry.fields);
      setDoctors(
        new Map(
          doctorEntries.map(e => [e.sys.id, {...e.fields, id: e.sys.id}]),
        ),
      );
    });
  }, []);

  const selectedDoctor = doctors.get(selectedDoctorId);

  const classes = useStyles();

  const handleCenterChange = center => {
    setCurrentCenter(center);
  };

  const selectDoctor = doctor => {
    window.history.pushState(null, null, '/' + doctor.id);
    mixpanel.track("select_doctor", {name: doctor.name, specialty: doctor.specialty, city: doctor.city});
    setSelectedDoctorId(doctor.id);
  };

  const unSelectDoctor = () => {
    setSelectedDoctorId(null);
    window.history.pushState(null, null, '/');
  };

  if (selectedDoctorId && mapContainerRef.current) {
    scrollToRef(mapContainerRef);
  }

  return (
      <div className={classes.root}>
        <AppBar className={classes.header}>
          <div style={{ display: "flex" }}>
            <a className={classes.headerImgButton} href="https://thenocturnists.com"><img className={classes.headerImg} src={header} alt="header" /></a>
          </div>
        </AppBar>
        <Landing
          handleScrollToMap={() => {scrollToRef(mapContainerRef)}}
          landingCopy={landingCopy}
        />
        <Hidden xsDown>
          <DesktopMapView
            mapRef={mapContainerRef}
            doctorsList={doctorsList}
            currentZoom={currentZoom}
            currentCenter={currentCenter}
            setHoverDoctor={setHoverDoctor}
            setZoom={zoom => setZoom(zoom)}
            setLastZoom={zoom => setLastZoom(zoom)}
            lastZoom={lastZoom}
            handleCenterChange={handleCenterChange}
            selectDoctor={d => selectDoctor(d)}
            hoverDoctor={hoverDoctor}
            selectedDoctor={selectedDoctor}
            selectedDoctorId={selectedDoctorId}
            mapCopy={mapCopy}l
          />
        </Hidden>
        <Hidden smUp>
          <MobileWebMapView
            mapRef={mapContainerRef}
            doctorsList={doctorsList}
            currentZoom={currentZoom}
            currentCenter={currentCenter}
            setHoverDoctor={setHoverDoctor}
            setZoom={zoom => setZoom(zoom)}
            setLastZoom={zoom => setLastZoom(zoom)}
            lastZoom={lastZoom}
            handleCenterChange={handleCenterChange}
            selectDoctor={d => selectDoctor(d)}
            hoverDoctor={hoverDoctor}
            selectedDoctor={selectedDoctor}
            selectedDoctorId={selectedDoctorId}
            setSelectedDoctorId={setSelectedDoctorId}
            mapCopy={mapCopy}l
          />
        </Hidden>
        <About aboutCopy={aboutCopy} />
        <Footer />
        <SwipeableDrawer
          anchor={'right'}
          open={selectedDoctorId}
          onClose={unSelectDoctor}
        >
          <DoctorDetail
            selectedDoctor={selectedDoctor}
            onBack={unSelectDoctor}
          />
        </SwipeableDrawer>
      </div>
  );
}


export const renderContentfulRichContent = (contentfulNode) => {
  if (!contentfulNode){
    return null;
  }
  const contentElementToReact = (contentElement) => {
    switch (contentElement.nodeType) {
      case "text":
        if (
          _.includes(contentElement.marks?.map(m => m.type), "bold") &&
          _.includes(contentElement.marks?.map(m => m.type), "italic")
        ) {
          return (
            <i>
              <strong>
                {contentElement.value}
              </strong>
            </i>
          );
        } else if (_.includes(contentElement.marks?.map(m => m.type), "italic")) {
          return (
            <i>
              {contentElement.value}
            </i>
          );
        } else if (_.includes(contentElement.marks?.map(m => m.type), "bold")) {
          return (
            <strong>
              {contentElement.value}
            </strong>
          );
        } else {
          return (
            <span>
              {contentElement.value}
            </span>
          );
        }
      case "hyperlink":
        const content = contentElement.content[0].value;
        const uri = contentElement.data.uri;
        const marks = contentElement.content[0].marks;
        if (
          _.includes(marks?.map(m => m.type), "bold") &&
          _.includes(marks?.map(m => m.type), "italic")
        ) {
          return (
            <a href={uri}>
              <strong>
                <i>
                  {content}
                </i>
              </strong>
            </a>
          );
        } else if (_.includes(marks?.map(m => m.type), "italic")) {
          return (
            <a href={uri}>
              <i>
                {content}
              </i>
            </a>
          );
        } else if (_.includes(marks?.map(m => m.type), "bold")) {
          return (
            <a href={uri}>
              <strong>
                {content}
              </strong>
            </a>
          );
        } else {
          return (
            <a href={uri}>
              {content}
            </a>
          );
        }
    }
  };

  return (
    <div>
      {contentfulNode.content.map(e => contentElementToReact(e))}
    </div>
  );
};
